// OWNED BY the matches implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/matches/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// התאמות (Matches) tab — mock s18 + Haim's review round. Two pill sub-tabs:
//  A. נכסים במיניסייט — the contact's curated set (favorite-listings-of-contact doc,
//     live via onSnapshot) with engagement stats/filters/fuzzy search, undo-able
//     removal, per-listing edit suite and quick view.
//  B. הוספת נכסים — pre-filtered server-side discovery (Postgres), seeded from the
//     contact's search profile, free-text query, stacked שלי/משרד/שוק sections,
//     scored transparently.
// All data plumbing and ported legacy logic lives under ./matches/.
//
// A third pill, המשרד, appears when colleagues in the viewer's office sent this customer a
// minisite of their own (./matches/OfficeMinisitesPanel — read only; the office asked to see what
// each agent sent before sending the same listing again).
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Stack, Typography, ButtonBase } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { useSnackbar } from "notistack5";
import Label from "components/Label";
import useContactWrites from "hooks/contacts/useContactWrites";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import type {
  RealEstateContact,
  ListingPageViewsDocument,
  FavoriteListingsOfContact
} from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";
import { TabCard } from "../TabCard";
import { setCached } from "../data/sessionCache";
import {
  viewsCacheKey,
  favoritesCacheKey,
  ensureMatchesLive,
  preloadMatchesData,
  useCachedLiveValue,
  favoriteListingsCacheKey,
  reconcileFavoriteListingsData,
  profileSuggestionDismissedKey
} from "./matches/matchesData";
import type { MatchHit } from "./matches/matchesSearch";
import {
  buildFiltersFromContact,
  seedBudgetForListedFor,
  areSearchFiltersEqual,
  buildContactProfileUpdates,
  filtersMatchContactProfile
} from "./matches/contactMatchFilters";
import type { MatchFilters } from "./matches/contactMatchFilters";
import { useMatchesSearch } from "./matches/useMatchesSearch";
import { getContactFirstName } from "./matches/matchesShare";
import { useMatchesWatermarkStatus } from "./matches/useMatchesWatermarkStatus";
import {
  useBulkWatermarkRemoval,
  MatchesWatermarkStatusChip,
  BulkWatermarkRemovalDialog
} from "./matches/BulkWatermarkRemoval";
import MinisiteSubTab from "./matches/MinisiteSubTab";
import AddListingsSubTab from "./matches/AddListingsSubTab";
import OfficeMinisitesPanel, { OfficeMinisitesAvatars } from "./matches/OfficeMinisitesPanel";
import { useOfficeMinisites } from "./matches/officeMinisitesData";

type MatchesSubTabId = "minisite" | "add" | "office";

// The tab strip keeps hidden tabs MOUNTED — visibility (the ?tab= param) gates the
// search work so a hidden Matches tab never churns searches.
function useIsMatchesTabActive(): boolean {
  const [searchParams] = useSearchParams();
  return (searchParams.get("tab") ?? "overview") === "matches";
}

function useMatchesLiveData(contact: RealEstateContact): {
  favorites: FavoriteListingsOfContact | null | undefined;
  views: ListingPageViewsDocument | null | undefined;
  listings: MatchHit[] | undefined;
} {
  const { user } = useAuth();

  // Ref-counted live listeners (shared with the badge hook); released on page unmount.
  useEffect(() => {
    if (!user?.email || !contact.id) return undefined;
    return ensureMatchesLive(user.email, contact.id);
  }, [user?.email, contact.id]);

  const favorites = useCachedLiveValue<FavoriteListingsOfContact | null>(
    favoritesCacheKey(contact.id)
  );
  const views = useCachedLiveValue<ListingPageViewsDocument | null>(viewsCacheKey(contact.id));
  const listings = useCachedLiveValue<MatchHit[]>(favoriteListingsCacheKey(contact.id));

  // Keep listing data aligned with the favorites doc's listingIds (fetch-missing only).
  const listingIdsKey = (favorites?.listingIds ?? []).join(",");
  const favoritesLoaded = favorites !== undefined;
  useEffect(() => {
    if (!favoritesLoaded) return;
    void reconcileFavoriteListingsData(contact.id, listingIdsKey ? listingIdsKey.split(",") : []).catch(
      () => undefined
    );
  }, [contact.id, listingIdsKey, favoritesLoaded]);

  return { favorites, views, listings };
}

function SubTabPill({
  label,
  count,
  adornment,
  active,
  onClick
}: {
  label: string;
  count?: number;
  /** Shown after the label in place of a count — the office pill's faces. */
  adornment?: ReactNode;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: 1.75,
        py: 0.75,
        borderRadius: 999,
        typography: "body2",
        fontWeight: active ? 600 : 500,
        gap: 0.75,
        color: active ? "text.primary" : "text.secondary",
        bgcolor: active ? "background.paper" : "transparent",
        boxShadow: active ? theme.customShadows.z8 : "none"
      }}
    >
      {label}
      {count !== undefined && count > 0 && <Label color="info">{count}</Label>}
      {adornment}
    </ButtonBase>
  );
}

export default function MatchesTab({ contact }: ContactTabComponentProps): JSX.Element {
  const theme = useTheme();
  const { updateContact } = useContactWrites();
  const { userSettings } = useFirestore();
  const { enqueueSnackbar } = useSnackbar();

  const isTabActive = useIsMatchesTabActive();
  const [chosenSubTab, setSubTab] = useState<MatchesSubTabId>("minisite");
  // The office pill exists only while colleagues have something to show; a choice of it that
  // outlived the answer (a colleague emptied their page) falls back to the viewer's own page.
  const office = useOfficeMinisites(contact.id);
  const showOffice = office.status === "ready" && office.minisites.length > 0;
  const subTab: MatchesSubTabId = chosenSubTab === "office" && !showOffice ? "minisite" : chosenSubTab;
  const [addQueryText, setAddQueryText] = useState("");

  const { favorites, views, listings } = useMatchesLiveData(contact);

  // ── filters: seeded from the contact profile, re-seeded only while untouched ──
  const seededFilters = useMemo(() => buildFiltersFromContact(contact), [contact]);
  const [filters, setFilters] = useState<MatchFilters>(seededFilters);
  const [filtersEdited, setFiltersEdited] = useState(false);
  const filtersEditedRef = useRef(false);

  useEffect(() => {
    // While the realtor hasn't touched the chips, live profile updates re-seed the
    // search. Once edited, their refinement is never clobbered (legacy context bug).
    // Full-tier comparison keeps the invariant airtight: unedited ⇒ filters are exactly
    // the contact seed, advanced (drawer-only) filters included.
    if (filtersEditedRef.current) return;
    setFilters((prev) => (areSearchFiltersEqual(prev, seededFilters) ? prev : seededFilters));
  }, [seededFilters]);

  const handleFiltersChange = (patch: Partial<MatchFilters>): void => {
    // Flipping למכירה/להשכרה re-derives the price bounds from the contact's budget for
    // the NEW type — carrying a sale-scale budget into a rent search guarantees an empty
    // result (and vice versa). The filters drawer opts into this same rule by omitting
    // the price keys when the realtor flipped the toggle without touching the price
    // fields (see drawerDraftToFiltersPatch).
    const next =
      patch.listedFor && patch.listedFor !== filters.listedFor && !("minPrice" in patch)
        ? { ...filters, ...patch, ...seedBudgetForListedFor(contact, patch.listedFor) }
        : { ...filters, ...patch };

    // A no-op apply must not count as an edit. The drawer commits its WHOLE draft on
    // search, so opening it and pressing חיפוש without changing anything arrives here as
    // a full — but identical — patch; marking that "edited" would permanently stop the
    // filters re-seeding from later profile changes, for an action the realtor
    // experiences as having done nothing.
    if (areSearchFiltersEqual(filters, next)) return;

    filtersEditedRef.current = true;
    setFiltersEdited(true);
    setFilters(next);
  };

  // ── reset back to the contact's own filters ───────────────────────────────────
  // The baseline is DERIVED (seededFilters, recomputed from the live contact), never a
  // snapshot taken at mount: if the search profile is edited elsewhere — the overview
  // tab, another browser tab, the MCP tools — "the contact's filters" must mean what
  // the profile says NOW, not what it said when this tab opened.
  //
  // Comparison is the FULL filter tier, so a filter that only the drawer can set counts
  // as divergence and is cleared by the reset too. Offered only when something actually
  // differs, so it never sits there as dead chrome on a freshly-seeded search.
  const canResetToContact = !areSearchFiltersEqual(filters, seededFilters);

  const handleResetToContactFilters = (): void => {
    // Back to profile-driven: clearing the edited flag also restores the "מסונן
    // אוטומטית לפי פרופיל החיפוש" caption, re-arms live re-seeding from the contact,
    // and drops the profile-update nudge (which is gated on filtersEdited).
    filtersEditedRef.current = false;
    setFiltersEdited(false);
    setFilters(seededFilters);
    // The new filters produce a new search key, so the search re-runs and the source
    // tab strip re-evaluates which source to open for the new result set — the same
    // path any other filter change takes.
  };

  // The search fan-out (one server request per origin) runs only when the
  // discovery surface actually needs it: the add sub-tab is open, or the minisite is
  // known-empty (its ghost tile funnels straight into the add flow, so pre-warm).
  // A visible-but-minisite-only Matches tab does NOT churn searches.
  const minisiteKnownEmpty =
    favorites !== undefined && (favorites?.listingIds?.length ?? 0) === 0;
  const searchEnabled = isTabActive && (subTab === "add" || minisiteKnownEmpty);
  const search = useMatchesSearch(contact, filters, searchEnabled, addQueryText);

  // ── subtle profile-update suggestion ──────────────────────────────────────────
  const suggestionDismissed = Boolean(
    useCachedLiveValue<boolean>(profileSuggestionDismissedKey(contact.id))
  );
  const profileDiverges = filtersEdited && !filtersMatchContactProfile(filters, contact);

  const handleAcceptProfileUpdate = async (): Promise<void> => {
    const updates = buildContactProfileUpdates(filters, contact);
    const inferredListedFor = buildFiltersFromContact(contact).listedFor;
    const keepFlip = filters.listedFor !== inferredListedFor;
    if (!updates) {
      // Nothing to write. Never silently reset an active listedFor flip — the re-seed
      // effect would revert the toggle the realtor just chose.
      if (!keepFlip) {
        filtersEditedRef.current = false;
        setFiltersEdited(false);
      }
      enqueueSnackbar("פרופיל החיפוש כבר מעודכן", { variant: "info" });
      return;
    }
    try {
      // The server edit (`useContactWrites`), never the browser-direct upsert — see the note on the same swap in
      // OverviewDetailsPanel's AboutClientField. This tab now also mounts inside the CRM
      // side panel, whose whole design is that `allContacts` is never loaded, and
      // `updateContact` resolves its prior state from exactly that array.
      await updateContact(contact.id, updates);
      if (keepFlip) {
        // Keep the rent/sale flip active (still "edited" so re-seeding won't revert
        // it), but align the filters with the just-written profile for this type —
        // the nudge disappears because the filters now equal the seeded state.
        const updatedContact = { ...contact, ...updates } as RealEstateContact;
        setFilters(buildFiltersFromContact(updatedContact, filters.listedFor));
      } else {
        // Filters re-derive from the (optimistically updated) profile on the next seed.
        filtersEditedRef.current = false;
        setFiltersEdited(false);
      }
      enqueueSnackbar("פרופיל החיפוש עודכן", { variant: "success" });
    } catch (err) {
      console.error("MatchesTab: failed to update contact search profile", err);
      enqueueSnackbar("עדכון פרופיל החיפוש נכשל", { variant: "error", autoHideDuration: 4000 });
    }
  };

  const isPaidTier = Boolean(userSettings?.advancedFeatures?.includes("listingsMatch"));
  const minisiteCount = favorites?.listingIds?.length ?? 0;

  // ── watermarks across the whole minisite ──────────────────────────────────────
  // The run lives HERE (not in the dialog) so closing the dialog or switching sub-tab
  // never kills it — the header chip keeps reporting progress either way.
  const [bulkWatermarkOpen, setBulkWatermarkOpen] = useState(false);
  const bulkWatermarkRun = useBulkWatermarkRemoval();
  const watermarkStatus = useMatchesWatermarkStatus(
    listings,
    // Live listeners only while the status is actually on screen (or a run needs them).
    isTabActive && (subTab === "minisite" || bulkWatermarkRun.isRunning)
  );
  const showWatermarkStatus = subTab === "minisite" || bulkWatermarkRun.isRunning;

  return (
    <TabCard>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        <Box
          sx={{
            p: 0.5,
            borderRadius: 999,
            display: "flex",
            gap: 0.5,
            bgcolor: alpha(theme.palette.grey[500], 0.12)
          }}
        >
          <SubTabPill
            label="נכסים במיניסייט"
            count={minisiteCount}
            active={subTab === "minisite"}
            onClick={() => setSubTab("minisite")}
          />
          <SubTabPill
            label="הוספת נכסים"
            active={subTab === "add"}
            onClick={() => setSubTab("add")}
          />
          {showOffice && (
            <SubTabPill
              label="המשרד"
              adornment={<OfficeMinisitesAvatars minisites={office.minisites} />}
              active={subTab === "office"}
              onClick={() => setSubTab("office")}
            />
          )}
        </Box>
        {showWatermarkStatus && (
          // Market-scoped by design: the realtor's own photos aren't assumed to need
          // removal, so they're not in the count — only in the dialog behind it.
          <MatchesWatermarkStatusChip
            market={watermarkStatus.market}
            ownPendingCount={watermarkStatus.own.pendingStatuses.length}
            isRunning={bulkWatermarkRun.isRunning}
            progress={bulkWatermarkRun.progress}
            onManage={() => setBulkWatermarkOpen(true)}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {subTab !== "office" && (
          <Typography variant="caption" color="text.secondary">
            כל שינוי כאן מתעדכן מיד במיניסייט של {getContactFirstName(contact) || "הלקוח"}
          </Typography>
        )}
      </Stack>

      <BulkWatermarkRemovalDialog
        open={bulkWatermarkOpen}
        onClose={() => setBulkWatermarkOpen(false)}
        market={watermarkStatus.market}
        own={watermarkStatus.own}
        run={bulkWatermarkRun}
      />

      {subTab === "minisite" ? (
        <MinisiteSubTab
          contact={contact}
          favorites={favorites}
          views={views}
          listings={listings}
          // Per-card watermark badge + single-listing removal reuse the SAME live status
          // that drives the header chip and the multi-listing dialog — one computation,
          // one truth, no extra listeners per card.
          watermarkStatuses={watermarkStatus.statuses}
          onSwitchToAdd={() => setSubTab("add")}
        />
      ) : subTab === "office" ? (
        <OfficeMinisitesPanel contactId={contact.id} ownListingIds={favorites?.listingIds ?? []} />
      ) : (
        <AddListingsSubTab
          contact={contact}
          favorites={favorites}
          search={search}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          filtersEdited={filtersEdited}
          canResetToContact={canResetToContact}
          onResetToContact={handleResetToContactFilters}
          queryText={addQueryText}
          onQueryTextChange={setAddQueryText}
          profileDiverges={profileDiverges}
          suggestionDismissed={suggestionDismissed}
          onAcceptProfileUpdate={handleAcceptProfileUpdate}
          onDismissProfileSuggestion={() =>
            setCached(profileSuggestionDismissedKey(contact.id), true)
          }
          isPaidTier={isPaidTier}
        />
      )}
    </TabCard>
  );
}

// Badge for the tab strip: minisite listing count, live from the shared favorites
// listener + session cache — never fetches per render.
export function useMatchesTabBadge(contact: RealEstateContact): TabBadge {
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.email || !contact?.id) return undefined;
    return ensureMatchesLive(user.email, contact.id);
  }, [user?.email, contact?.id]);
  const favorites = useCachedLiveValue<FavoriteListingsOfContact | null>(
    favoritesCacheKey(contact.id)
  );
  const count = favorites?.listingIds?.length ?? 0;
  return count > 0 ? { count, color: "primary" } : null;
}

// Cache warm-up fired on contacts-table row hover and page mount. One-shot reads only
// (favorites doc → views doc + listing data); the discovery search is not pre-fired
// here — it needs the filters. Never throws, dedups via fetchCached.
export function preloadMatchesTab(contact: RealEstateContact): void {
  try {
    preloadMatchesData(contact);
  } catch {
    // preloads are best-effort by contract
  }
}
